import type { Row } from '@tanstack/react-table';
import type { AgendaRecordItem, GetAgendaRecordsRes, InlineRecordType, Schema, DataSchema } from '../../../types';
import { FieldWrapper, Inner, StyledCard } from './CatalogueWidgetItem.styles';
import type { Dispatch, SetStateAction } from 'react';
import { memo, useMemo } from 'react';
import { Checkbox } from '../../mui/common/Checkbox.tsx';
import type { ViewItem } from '../../../types/catalogViewItem.ts';
import { useFormContext } from 'react-hook-form';
import { renderComponentByType } from '../../../utils/catalogueWidget/renderComponentByType.tsx';
import { modify } from '../../../utils/catalogueWidget/updateCatalogData.ts';

interface CatalogueWidgetItemProps<T extends keyof P, P extends DataSchema> {
  row: Row<Schema<T, P>>;
  selectedRows: string[];
  handleRowSelect: (rowId: string, isChecked: boolean) => void;
  viewModel?: ViewItem[];
  buttonActionHandler: (viewItem: ViewItem, row: Row<Schema<T, P>>, viewModel?: ViewItem[], attributes?: InlineRecordType[]) => void | null;
  setCatalogData?: Dispatch<SetStateAction<GetAgendaRecordsRes<AgendaRecordItem[]>>>;
}

const CatalogueWidgetItemBase = <T extends keyof P, P extends DataSchema>({
  row,
  viewModel,
  selectedRows,
  handleRowSelect,
  buttonActionHandler,
  setCatalogData,
}: CatalogueWidgetItemProps<T, P>) => {
  const form = useFormContext();
  const basePath = `items.${row.original.id}`;
  const isSelected = selectedRows.includes(row.original.id);

  const gridLayout = useMemo(() => {
    return (viewModel ?? []).reduce(
      (acc, item) => ({
        columns: Math.max(acc.columns, (item.column ?? 1) + Math.max(1, item.columnSpan ?? 1) - 1),
        rows: Math.max(acc.rows, (item.row ?? 1) + Math.max(1, item.rowSpan ?? 1) - 1),
      }),
      { columns: 1, rows: 1 }
    );
  }, [viewModel]);

  const handleSetCatalogData = (viewItem: ViewItem, fieldName: string, value: string | number) => {
    const parsedId = fieldName.split('.')[1];

    setCatalogData?.((prev) => {
      const itemIndex = prev.items?.findIndex((item) => item.id === parsedId);

      if (itemIndex !== -1) {
        const attributeIndex = prev?.items?.[itemIndex]?.attributes?.findIndex((attribute) => attribute.formItem?.id === viewItem?.id);

        if (attributeIndex !== -1) {
          const modified = { ...prev };
          const attribute = {
            ...modified?.items?.[itemIndex]?.attributes?.[attributeIndex],
          };
          const updated = modify(viewItem.kind as string, attribute, viewItem?.kind === 'Number' ? Number(value) : value);

          return {
            ...modified,
            items: modified.items.map((it, idx) =>
              idx === itemIndex
                ? {
                    ...it,
                    attributes: it.attributes.map((attr, aIdx) => (aIdx === attributeIndex ? updated : attr)),
                  }
                : it
            ),
          };
        }
      }

      return prev;
    });
  };

  return (
    <StyledCard>
      <Inner gridColumns={gridLayout.columns} gridRows={gridLayout.rows}>
        <Checkbox
          style={{ position: 'absolute', top: 0, left: 0 }}
          checked={isSelected}
          onClick={() => handleRowSelect(row.original.id, !isSelected)}
        />
        {viewModel?.map((viewItem) => {
          const column = viewItem.column ?? 1;
          const rowIndex = viewItem.row ?? 1;
          const columnSpan = Math.max(1, viewItem.columnSpan ?? 1);
          const rowSpan = Math.max(1, viewItem.rowSpan ?? 1);

          return (
            <FieldWrapper
              key={viewItem.id}
              style={{
                gridColumn: `${column} / ${column + columnSpan}`,
                gridRow: `${rowIndex} / ${rowIndex + rowSpan}`,
              }}>
              {renderComponentByType(
                {
                  ...viewItem,
                  name: `${basePath}.${viewItem.name}`,
                  title: viewItem.itemSchema?.formItemDefinition?.button?.name ?? viewItem.name,
                },
                form,
                (buttonViewItem, attributes) => buttonActionHandler(buttonViewItem, row, viewModel, attributes),
                (fieldName, value) => handleSetCatalogData(viewItem, fieldName, value)
              )}
            </FieldWrapper>
          );
        })}
      </Inner>
    </StyledCard>
  );
};

export const CatalogueWidgetItem = memo(CatalogueWidgetItemBase) as typeof CatalogueWidgetItemBase;
